import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Extend Window interface for gtag
declare global {
    interface Window {
        dataLayer: any[];
        gtag: (...args: any[]) => void;
    }
}

const GA_TRACKING_ID = 'G-2V3W9BGQXD';

// gtag.js is loaded and configured in index.html with send_page_view: false,
// so this component is the only source of page_view (one per route change).
// Keep "Page changes based on browser history events" OFF in the GA4 stream's
// enhanced measurement, or SPA navigations get counted twice.
export const GoogleAnalytics = () => {
    const location = useLocation();

    useEffect(() => {
        // Initialize gtag if not already present (e.g. index.html snippet blocked)
        if (!window.gtag) {
            window.dataLayer = window.dataLayer || [];
            window.gtag = function gtag() {
                window.dataLayer.push(arguments);
            };
            window.gtag('js', new Date());
            window.gtag('config', GA_TRACKING_ID, { send_page_view: false });
        }

        // Click tracking: any element with data-track="<id>" sends a button_click
        // event. Capture phase so dialogs that stopPropagation still report.
        // Never put user-typed text or PII in data-track / data-track-label.
        const onClick = (e: MouseEvent) => {
            const el = (e.target as Element | null)?.closest?.<HTMLElement>('[data-track]');
            if (!el) return;
            trackClick(el.dataset.track as string, el.dataset.trackLabel);
        };
        document.addEventListener('click', onClick, true);
        return () => document.removeEventListener('click', onClick, true);
    }, []);

    // Track page views on route change
    useEffect(() => {
        trackPageView(location.pathname + location.search);
    }, [location]);

    return null;
};

// Utility functions for event tracking
export const trackEvent = (action: string, params: Record<string, string | number | undefined> = {}) => {
    if (window.gtag) {
        window.gtag('event', action, params);
    }
};

export const trackClick = (buttonId: string, label?: string) =>
    trackEvent('button_click', { button_id: buttonId, button_label: label, page_path: window.location.pathname });

export const trackPageView = (url: string) =>
    trackEvent('page_view', { page_path: url, page_location: window.location.href, page_title: document.title });

export const trackUserSignup = (method: string) => trackEvent('sign_up', { method });

export const trackUserLogin = (method: string) => trackEvent('login', { method });

// No app name: it is user-typed and may contain personal data.
export const trackAppCreation = (source: 'prompt' | 'template') => trackEvent('create_app', { source });

export const trackAppPublish = () => trackEvent('publish_app');

export const trackTemplateUse = (templateId: string) => trackEvent('use_template', { template_id: templateId });
